import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getHome } from "@/lib/api";
import HomeHero from "@/components/HomeHero";
import BookCard from "@/components/BookCard";
import AuthorCard from "@/components/AuthorCard";
import Newsletter from "@/components/Newsletter";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";


function SectionHead({ n, title, to, cta }) {
  return (
    <div className="flex items-end justify-between gap-4 border-t border-[#0f2544]/15 pt-6 mb-10">
      <div className="flex items-baseline gap-4">
        <span className="hv-overline">{n}</span>
        <h2 className="font-serif text-3xl md:text-5xl font-semibold tracking-tight text-[#0f2544]">{title}</h2>
      </div>
      {to && <Link to={to} className="hv-overline hover:text-[#0f2544] transition-colors whitespace-nowrap hidden sm:block">{cta} →</Link>}
    </div>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useEffect(() => { getHome().then(setData).catch(() => setData({ featured_books: [], featured_authors: [], resources: [] })); }, []);

  const books = data?.featured_books || [];
  const authors = data?.featured_authors || [];
  const resources = data?.resources || [];

  return (
    <>
      <Seo title="Donde comienza la excelencia" description="Editorial internacional de literatura cristiana y obras con propósito." jsonLd={{ "@context": "https://schema.org", "@type": "Organization", name: "Editorial Harvest", legalName: "Harvest Books Ltd.", slogan: "Donde comienza la excelencia" }} />

      <HomeHero data={data} />

      {/* ÚLTIMOS LANZAMIENTOS */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24" data-testid="section-lanzamientos">
        <Reveal><SectionHead n="01" title="Últimos lanzamientos" to="/libros" cta="Ver catálogo" /></Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
          {books.slice(0, 5).map((b, i) => <Reveal key={b.slug} delay={(i % 5) * 0.05}><BookCard book={b} /></Reveal>)}
        </div>
      </section>

      {/* AUTORES */}
      <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24" data-testid="section-autores">
        <Reveal><SectionHead n="02" title="Autores" to="/autores" cta="Todos los autores" /></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {authors.slice(0, 4).map((a, i) => <Reveal key={a.slug} delay={(i % 4) * 0.06}><AuthorCard author={a} /></Reveal>)}
        </div>
      </section>

      {/* GRATIS — solo si hay */}
      {resources.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-16 md:py-24" data-testid="section-recursos">
          <Reveal><SectionHead n="03" title="Gratis" to="/gratis" cta="Ver todos" /></Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-8">
            {resources.map((r) => (
              <div key={r.id} className="hv-book-hover" data-testid={`home-resource-${r.slug}`}>
                <div className="hv-book">{r.portada ? <img src={r.portada} alt={r.titulo} /> : <div className="w-full h-full bg-[#e7e5df]" />}</div>
                <h3 className="font-serif text-base font-semibold mt-3 text-[#0f2544]">{r.titulo}</h3>
                {r.autor && <p className="text-[0.8rem] text-[#8a8a83]">{r.autor}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PUBLICA CTA */}
      <section className="bg-[#0f2544] text-white" data-testid="section-publicar">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-10 md:gap-16 items-end">
          <Reveal>
            <p className="hv-overline hv-overline-light">Publica con Harvest</p>
            <h2 className="font-serif text-5xl md:text-7xl font-semibold tracking-tight leading-[0.92] mt-5">¿Tienes un libro?</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-white/60 text-lg leading-relaxed">Acompañamos cada manuscrito durante todo el proceso editorial para que tu obra alcance la calidad que merece.</p>
            <Link to="/publica-con-harvest#enviar" className="group inline-flex items-center gap-2 mt-8 bg-white px-8 py-4 text-xs font-semibold tracking-[0.14em] uppercase text-[#0f2544] hover:bg-[#1f44e0] hover:text-white transition-colors" data-testid="quiero-publicar">
              Envía tu manuscrito <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Newsletter />
    </>
  );
}
